import * as React from 'react';
import { useDialogs } from '@toolpad/core/useDialogs';
import { DialogProps } from '@toolpad/core/useDialogs';
import {
  Box,
  Dialog,
  Button,
  Checkbox,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
} from '@mui/material';

type DeleteDialogPayload = { entityLabel?: string };
type DeleteDialogResult = { confirmed: boolean; hardDelete: boolean };

type HardDeleteConfirmPayload = { entityLabel?: string };
type HardDeleteConfirmResult = { confirmed: boolean };

function DeleteDialogNode({
  open,
  onClose,
  payload,
}: DialogProps<DeleteDialogPayload, DeleteDialogResult>) {
  const [hardDelete, setHardDelete] = React.useState(false);
  const noun = payload?.entityLabel ?? 'item';

  const handleCancel = () => {
    onClose({ confirmed: false, hardDelete: false });
  };

  const handleDelete = () => {
    onClose({ hardDelete, confirmed: true });
  };

  return (
    <Dialog fullWidth open={open} maxWidth="xs" onClose={handleCancel}>
      <DialogTitle>Delete {noun}</DialogTitle>
      <DialogContent>
        <Typography gutterBottom variant="body1">
          Are you sure you want to delete this {noun}?
        </Typography>

        <Box
          sx={{
            mt: 2,
          }}
        >
          <FormControlLabel
            label="Hard delete (permanent)"
            control={
              <Checkbox
                checked={hardDelete}
                onChange={(e) => setHardDelete(e.target.checked)}
              />
            }
          />
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            If you choose hard delete, this action cannot be undone. All related
            data will be permanently removed and cannot be restored later.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleDelete}>
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function HardDeleteConfirmDialogNode({
  open,
  onClose,
  payload,
}: DialogProps<HardDeleteConfirmPayload, HardDeleteConfirmResult>) {
  const noun = payload?.entityLabel ?? 'item';

  const handleCancel = () => onClose({ confirmed: false });
  const handleConfirm = () => onClose({ confirmed: true });

  return (
    <Dialog fullWidth open={open} maxWidth="xs" onClose={handleCancel}>
      <DialogTitle>Confirm hard delete</DialogTitle>
      <DialogContent>
        <Typography gutterBottom variant="body1">
          You are about to perform a HARD DELETE for this {noun}.
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          This action is permanent and cannot be undone. All data associated
          with this {noun} will be permanently removed. Are you absolutely sure
          you want to continue?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel}>Cancel</Button>
        <Button color="error" variant="contained" onClick={handleConfirm}>
          Yes, hard delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export async function confirmDeleteWithHardOption(
  dialogs: ReturnType<typeof useDialogs>,
  params?: { entityLabel?: string }
): Promise<{ confirmed: boolean; hardDelete: boolean }> {
  const payload: DeleteDialogPayload = { entityLabel: params?.entityLabel };

  const deleteResult = await dialogs.open(DeleteDialogNode, payload);

  if (!deleteResult.confirmed) {
    return { confirmed: false, hardDelete: false };
  }

  if (!deleteResult.hardDelete) {
    return { confirmed: true, hardDelete: false };
  }

  const confirmResult = await dialogs.open(HardDeleteConfirmDialogNode, {
    entityLabel: params?.entityLabel,
  } as HardDeleteConfirmPayload);

  if (!confirmResult.confirmed) {
    return { confirmed: false, hardDelete: false };
  }

  return { confirmed: true, hardDelete: true };
}
